import { createColumnHelper, flexRender, getCoreRowModel, useReactTable } from "@tanstack/react-table";
import { ExportCategories } from "PFApp/booking/types/export_booking";
import { useExportData } from "PFCore/hooks/queries/bookings/booking_export/use_export_data";
import React, { useMemo } from "react";

import { useExportWizardModalContext } from "../../../export_wizard_modal_provider";
import { getCategoryTitle } from "../../export_fields_utils";
import { CreateNewTemplate } from "./create_new_template/create_new_template";
import css from "./export_tab.module.scss";
import { ReportNameModal } from "./report_name_modal";
import { ScheduleSettingsModal } from "./schedule_settings_modal";

const getCategoryKey = (id = "") => id.split("-")[0];
const getFieldKey = (id = "") => id.split("-")[1];

const getCellValue = (categories: ExportCategories, id: string) =>
  categories[getCategoryKey(id)][getFieldKey(id)]?.value || "";
const getHeaderValue = (id: string, title: string): JSX.Element => (
  <>
    {getCategoryTitle(id)} <span>{title}</span>
  </>
);

const columnHelper = createColumnHelper<ExportCategories>();

export const ExportTab = (): JSX.Element => {
  const {
    state: {
      data: {
        selectedFields,
        modals: { reportNameOpen, scheduleSettingsOpen }
      }
    }
  } = useExportWizardModalContext();

  const { data } = useExportData();

  const columns = useMemo(
    () =>
      selectedFields.map(({ id = "", title }) =>
        columnHelper.accessor((row) => getCellValue(row, id), {
          id,
          cell: (info) => info.getValue(),
          header: () => getHeaderValue(id, title)
        })
      ),
    [selectedFields]
  );

  const table = useReactTable({
    data: data?.entries || [],
    columns,
    getCoreRowModel: getCoreRowModel()
  });

  return (
    <>
      <CreateNewTemplate />
      <div className={css.backlight}>
        <div className={css.scrollContainer}>
          <table className={css.exportTable}>
            <thead>
              {table.getHeaderGroups().map((headerGroup) => (
                <tr key={headerGroup.id}>
                  {headerGroup.headers.map((header) => (
                    <th key={header.id}>{flexRender(header.column.columnDef.header, header.getContext())}</th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody>
              {table.getRowModel().rows.map((row) => (
                <tr key={row.id}>
                  {row.getVisibleCells().map((cell) => (
                    <td key={cell.id}>{flexRender(cell.column.columnDef.cell, cell.getContext())}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      {reportNameOpen && <ReportNameModal />}
      {scheduleSettingsOpen && <ScheduleSettingsModal />}
    </>
  );
};
